/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
'use memo';
import cx from 'classnames';
import { MAX_VISIBLE_THUMBNAILS } from '@/components/Carousel/constants';
import ThumbnailComponent from '@/components/Carousel/ThumbnailComponent';
import type {
  CarouselItem,
  CarouselItemTypes,
} from '@/components/Carousel/ThumbnailComponent/types';
import { eventConstants } from '@/constants/event';
import { useLabels } from '@/hooks/useLabels';
import { useLocale } from '@/hooks/useLocale';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import React, { useRef, useState, Suspense, useCallback } from 'react';
import { useGcpProductVideos } from '../../api/getGcpProductVideos';
import { CarouselComponent } from './CarouselComponent';
import styles from './styles.module.scss';
import { map360CarouselItem } from './utils/map360CarouselItem';
import { type ProductModel } from '@/api/types/products-types';
import ProgressModal from '@/components/AZCustomComponent/ProgressModal';
import { useFeatureFlag } from '@/features/globalConfig';
import dynamic from 'next/dynamic';
import { YMMESpecificInstallVideoBanner } from '@/components/ProductDetail/ProductAvailabilityInfo/YMMESpecificInstallVideoBanner/YMMESpecificInstallVideoBanner';
import { useSylvaniaVideos } from '@/features/product/api/getSylvaniaVideos';
import { trackImageInteractionPDP } from '../../utils/analytics/trackImageInteractionPDP';
import { ConfigurableSkuBadge } from '@/features/configurableSku/components/ConfigurableSkuBadge';
import { useGetGcp360Image } from '../../api/getGcp360Image';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useGetGcpProductVehicleImage } from '../../api/getGcpProductVehicleImage';
import { useGetAcesGcpProductVehicleImage } from '../../api/getAcesGcpProductVehicleImage';
import { usePreferredVehicle } from '@/features/header/hooks/usePreferredVehicle';
import { useContentStackDefaultPDPContent } from '../../api/getContentStackDefaultProductContent';
import { clickTrack } from '@/utils/analytics/clickTrack';
import { pageTypes } from '@/utils/analytics/legacyUtils/pageTypes';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { analyticsDynamicPageName } from '@/utils/analytics/legacyUtils/analyticsDynamicPageName';
import VehicleSpecificImagesBanner from '@/components/ProductDetail/ProductAvailabilityInfo/VehicleSpecificImagesBanner/VehicleSpecificImagesBanner';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { getImagesWithDefault } from '../../utils/productImageUtils';
import { useAcesSylvaniaVideos } from '@/features/product/api/getAcesSylvaniaVideo';
import { countryCodes } from '@/constants/locale';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';

const LazyProductImageModal = dynamic(
  () => import('./ProductImageModal').then((mod) => mod.ProductImageModal),
  {
    loading: () => <ProgressModal noScroll />,
  },
);

const LazyEnlargedMediaComponent = dynamic(() =>
  import('./EnlargedMediaComponent').then((mod) => mod.EnlargedMediaComponent),
);

type Props = {
  data: ProductModel;
  labels: Record<string, string>;
};

const labelMap = {
  label_CategoryPage_body_ImageNotAvailable: 'label_CategoryPage_body_ImageNotAvailable',
  hoverToZoomLabel: 'label_hover_over_image_to_zoom',
  tapToZoomLabel: 'Label_tap_image_to_zoom',
  tapImage360Label: 'label_tap_image_to_view_360',
  dragImageLabel: 'label_drag_image_to_view_all_slides',
};

export const ProductCarousel = (props: Props) => {
  const {
    itemDescription,
    itemMediaUrl,
    itemId,
    originalPartTypeId,
    location,
    cmsContentPath,
    taxonomyUrl,
    partNumber,
    partGroupId,
  } = props.data;
  const isMobileDevice = useMediaQuery((theme) => theme.breakpoints.only('sm'));
  const labels = useLabels(labelMap);
  const locale = useLocale();
  const country = getCountryFromLocale(locale);
  const { acesEnabled } = useAcesFlags();
  const initializedImages = useRef(0);
  const [modalTab, setModalTab] = useState('images');
  const [pinchToZoomImagesInitialized, setPinchToZoomImagesInitialized] = useState(false);
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));
  const { hoverToZoomLabel, tapToZoomLabel, tapImage360Label, dragImageLabel } = labels;
  const [selectedModalThumbnailIndex, setSelectedModalThumbnailIndex] = useState(0);
  const [selectedPDPThumbnailIndex, setSelectedPDPThumbnailIndex] = useState(0);
  const [aprimoVideoTag, setAprimoVideoTag] = useState<HTMLVideoElement | null>(null);
  const productImages = (itemMediaUrl?.productImages as string[]) || [];
  const defaultImage = itemMediaUrl?.defaultItemImageUrl;
  const imagesWithDefault = acesEnabled ? getImagesWithDefault(productImages, defaultImage) : [];
  const enlargedImages = acesEnabled
    ? imagesWithDefault
    : itemMediaUrl?.enlargedItemImageUrls || [];

  const imageThumbnails = acesEnabled
    ? imagesWithDefault
    : itemMediaUrl?.thumbnailItemImageUrls || [];

  const imageData = enlargedImages?.map(mapToImageObject) ?? [];
  const isVehicleSpecificImagesBannerEnabled = useForteDecisionFlagWithTrueDefault(
    'vehicleSpecificImagesBannerEnabled',
  );
  const isUsOrMxSite = locale === countryCodes.us || locale === countryCodes.mx;
  const { data: threeSixtyImage } = useGetGcp360Image({
    country,
    skuIds: itemId,
    enabled: isUsOrMxSite && Boolean(itemId),
  });
  const threeSixtyImageData = Array.isArray(threeSixtyImage) ? threeSixtyImage[0] : threeSixtyImage;
  const threeSixtyUrls = threeSixtyImageData?.urls ?? [];
  const urls =
    threeSixtyUrls.length > 0
      ? threeSixtyImageData?.thumbnail
        ? [threeSixtyImageData.thumbnail, ...threeSixtyUrls]
        : threeSixtyUrls
      : undefined;

  const threeSixtyImageThumbnails = map360CarouselItem(
    locale,
    threeSixtyImageData?.thumbnail ? { thumbnail: threeSixtyImageData.thumbnail } : undefined,
    itemDescription,
  );

  const {
    data: productDetailsData,
    isSuccess: isProductDetailsSuccess,
    isFetching: isProductDetailsFetching,
  } = useProductDetails();
  const isProductDetailsReady = isProductDetailsSuccess && !isProductDetailsFetching;

  const taxonomyPath = cmsContentPath ?? taxonomyUrl;
  const { data: defaultPDPContent } = useContentStackDefaultPDPContent({
    enabled: isProductDetailsReady,
    taxonomyPath,
  });
  const partTypeHasVehicleImages =
    defaultPDPContent?.partTypesWithVehicleImages?.includes(originalPartTypeId ?? '') ?? false;
  const acesPartTypeHasVehicleImages =
    defaultPDPContent?.partTypesWithVehicleImages?.includes(partGroupId ?? '') ?? false;
  const vehicleData = usePreferredVehicle();
  const isProductVehicleImageEnabled =
    useFeatureFlag('IS_PRODUCT_VEHICLE_IMAGE_ENABLED') === 'true';

  const { data: partsProProductVehicleImage } = useGetGcpProductVehicleImage({
    partCode: originalPartTypeId ?? '',
    country,
    vehicleId: vehicleData?.catalogVehicleId ?? '',
    enabled: isProductVehicleImageEnabled && !!vehicleData && partTypeHasVehicleImages,
  });

  const { data: acesProductVehicleImage } = useGetAcesGcpProductVehicleImage({
    partCode: partGroupId ?? '',
    country,
    year: vehicleData?.year ?? '',
    makeId: vehicleData?.makeId ?? undefined,
    modelId: vehicleData?.modelId ?? undefined,
    enabled: isProductVehicleImageEnabled && !!vehicleData && acesPartTypeHasVehicleImages,
  });

  const productVehicleImage = acesEnabled ? acesProductVehicleImage : partsProProductVehicleImage;

  const installVideosEnabled = useFeatureFlag('IS_YMME_INSTALL_VIDEOS_ENABLED') === 'true';
  const { data: partsProSylvaniaData } = useSylvaniaVideos({
    enabled: installVideosEnabled && !acesEnabled,
    productId: props.data.itemId,
  });

  const { data: acesSylvaniaData } = useAcesSylvaniaVideos({
    enabled: installVideosEnabled && acesEnabled,
    skuId: props.data.itemId,
  });

  const sylvaniaData = acesEnabled ? acesSylvaniaData : partsProSylvaniaData;

  const installationGuideEnabled = Boolean(installVideosEnabled && !!sylvaniaData);

  const isPdpRedesignEnabled = useForteDecisionFlagWithTrueDefault('pdpRedesignEnabled');

  const gcpProductsVideosResult = useGcpProductVideos({
    country,
    skuId: itemId,
    enabled: true,
  });

  const videoThumbnails: CarouselItem[] =
    gcpProductsVideosResult.data?.map(({ thumbnailURL, videoURL, title }) => ({
      thumbnail: thumbnailURL,
      enlargedMediaUrl: videoURL,
      title,
      type: 'video' as const,
    })) ?? [];

  const videoThumbnailsExist = !!videoThumbnails.length;
  const allThumbnails = [...imageData, ...videoThumbnails, ...threeSixtyImageThumbnails];

  function mapToImageObject(enlargedMediaUrl: string, index: number): CarouselItem {
    return {
      alt: itemDescription,
      enlargedMediaUrl,
      mediumImageURL: index === 0 ? getMediumImageURL(enlargedMediaUrl) : undefined,
      thumbnail: acesEnabled ? getThumbnailImageURL(enlargedMediaUrl) : imageThumbnails[index],
      type: 'image',
    };
  }

  function getThumbnailImageURL(imageURL: string) {
    if (imageURL) {
      return `${imageURL}`;
    }

    return imageURL;
  }

  function getMediumImageURL(imageURL: string | undefined) {
    if (
      imageURL &&
      /^https:\/\/(st-|pr-|qa-|dv-)?contentinfo\.autozone\.com\/znetcs\/product-info\/([^/]+\/){4}image\/\d+\/$/.test(
        imageURL,
      )
    ) {
      return imageURL.replace(/\/\d+\/$/, '/8/');
    }

    return imageURL;
  }

  const onPinchToZoomImageInit = () => {
    initializedImages.current = initializedImages.current + 1;
    if (initializedImages.current === enlargedImages.length) {
      //avoid jumpy centering on mount of pinch to zoom images
      setTimeout(() => {
        setPinchToZoomImagesInitialized(true);
      }, 100);
    }
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [enlargedMediaData, setEnlargedMediaData] = useState(
    allThumbnails[0] ? allThumbnails[0] : null,
  );
  const enlargedMedia = enlargedMediaData ?? (allThumbnails[0] ? allThumbnails[0] : undefined);
  const enlargedMediaIsImage = enlargedMedia?.type === 'image';
  const enlargedMediaIs360 = enlargedMedia?.type === 'spin360';
  const imageLabel = isDesktop ? hoverToZoomLabel : tapToZoomLabel;
  const image360Label = !isMobileDevice ? dragImageLabel : tapImage360Label;
  const imageCaption = enlargedMediaIsImage ? imageLabel : enlargedMediaIs360 ? image360Label : '';

  const additionalThumbnails = [
    ...(videoThumbnailsExist ? [videoThumbnails[0]] : []),
    ...threeSixtyImageThumbnails,
  ];
  const imageCutOff = MAX_VISIBLE_THUMBNAILS - additionalThumbnails.length - 1; //one thumbnail for see more button + any video or threesixty images that need to show before see more button

  const imageThumbnailLength = imageThumbnails?.length;
  const additionalThumbnailLength = additionalThumbnails.length;
  const swapLastPDPImageWithVideo =
    !isMobileDevice && imageThumbnailLength > imageCutOff && additionalThumbnailLength; //if the number of images + videos or 360 images will cause the see more button to show up, replace the last image with the first video

  const pdpThumbnails = swapLastPDPImageWithVideo
    ? [
        ...allThumbnails.slice(0, imageCutOff),
        ...additionalThumbnails,
        ...allThumbnails.slice(imageCutOff, MAX_VISIBLE_THUMBNAILS - 1),
      ]
    : allThumbnails;

  const findThumbnailIndex = (thumbnails: CarouselItem[], thumbnailSrc?: string) => {
    return thumbnails.findIndex(
      (thumbnailObject: CarouselItem) => thumbnailObject.thumbnail === thumbnailSrc,
    );
  };

  const pdpBreadcrumb = productDetailsData?.product?.breadCrumbs;
  const pageTypeData = 'pdp';
  const pageName = analyticsDynamicPageName(pdpBreadcrumb, pageTypeData);
  const handleModalTabChange = (tab: string) => {
    setModalTab(tab);
    let selectedCarouselItem;
    if (tab === '360 Image') {
      selectedCarouselItem = threeSixtyImageThumbnails[0];
    } else if (tab === 'Videos') {
      selectedCarouselItem = videoThumbnails[0];
    } else if (tab === 'Port Locator') {
      const productVehicleImageThumbnail =
        productVehicleImage?.images.map(({ imageURL }, index) =>
          mapToImageObject(imageURL, index),
        ) ?? [];
      selectedCarouselItem = productVehicleImageThumbnail[0];
      if (isProductVehicleImageEnabled && !!vehicleData && partTypeHasVehicleImages) {
        clickTrack({
          customLinkName: 'Vehicle Specific Image View',
          eventType: 'vehicleSpecificImageView',
          pageName: `az:${pageName}-${partNumber}`,
          pageType: pageTypes.pdp,
          siteSection: 'catalog',
          productId: partNumber,
        });
      }
    } else {
      selectedCarouselItem = imageData[0];
    }
    showExpandedImageModal(0, selectedCarouselItem);
  };

  const selectModalTab = (type?: CarouselItemTypes) => {
    if (type === 'spin360') {
      setModalTab('360 Image');
      return;
    } else if (type === 'video') {
      setModalTab('Videos');
      return;
    }
    setModalTab('Images');
  };

  const showExpandedImageModal = (index: number, carouselItem: CarouselItem) => {
    const { thumbnail } = carouselItem;
    const pdpIndex: number = findThumbnailIndex(pdpThumbnails, thumbnail);
    setSelectedModalThumbnailIndex(index);
    setSelectedPDPThumbnailIndex(pdpIndex);
    setEnlargedMediaData(carouselItem);
  };

  const showExpandedImagePDP = (index: number, carouselItem: CarouselItem) => {
    const { type, thumbnail } = carouselItem;
    const dataSet = type === 'image' ? imageData : videoThumbnails;
    const modalIndex: number = findThumbnailIndex(dataSet, thumbnail);
    selectModalTab(carouselItem.type);
    setSelectedPDPThumbnailIndex(index);
    setSelectedModalThumbnailIndex(modalIndex);
    setEnlargedMediaData(carouselItem);
  };

  const handleClose = () => {
    setPinchToZoomImagesInitialized(false);
    initializedImages.current = 0;
    setModalOpen(false);
  };

  const handleOnKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (
      event.keyCode === eventConstants.enterKeyCode &&
      event.key === eventConstants.enterKeyType
    ) {
      return setModalOpen(true);
    }
  }, []);

  const handleOpenCarouselModal = useCallback(() => {
    setModalOpen(true);

    if (aprimoVideoTag) {
      aprimoVideoTag.pause();
    }
  }, [aprimoVideoTag]);

  const handleThumbnailClick = (item: CarouselItem) => {
    const index = findThumbnailIndex(pdpThumbnails, item.thumbnail);

    trackImageInteractionPDP(`PDP|${index + 1}`, `${index + 1}`);
    //open modal on desktop or for 360 thumbnails on tablet
    const callback = isDesktop
      ? handleOpenCarouselModal
      : (thumbnail: CarouselItem) => {
          if (thumbnail.type === 'spin360') {
            handleOpenCarouselModal();
          }
        };
    callback(item);
  };

  const handleSeeMoreClick = () => {
    handleOpenCarouselModal();
    trackImageInteractionPDP('See More Images-PDP');
  };

  const handleVehicleSpecificImagesBannerClick = () => {
    setModalTab('Port Locator');
    handleOpenCarouselModal();
    clickTrack({
      customLinkName: 'Vehcile Specific Image View',
      eventType: 'vehicleSpecificImageView',
      pageName: `az:${pageName}-${partNumber}`,
      pageType: pageTypes.pdp,
      siteSection: 'catalog',
      productId: partNumber,
    });
  };

  const shouldDisplaySylvaniaInstallGuideBanner =
    Boolean(props.data.vehicleFitFlag) && props.data.lineCode === 'SYL' && installationGuideEnabled;

  const shouldDisplayVehicleSpecificImagesBanner =
    isVehicleSpecificImagesBannerEnabled &&
    locale === 'en-US' &&
    Boolean(props.data.vehicleFitFlag) &&
    (productVehicleImage?.images?.length ?? 0) > 0;

  if (allThumbnails.length > 0) {
    return (
      <div
        className={cx(styles.productCarouselContainer, {
          [styles.productCarouselContainerV1]: !isPdpRedesignEnabled,
        })}
        id="productCarouselContainer"
      >
        {modalOpen ? (
          <Suspense fallback={<ProgressModal noScroll />}>
            <LazyProductImageModal
              open={modalOpen}
              imageCaption={imageCaption}
              imageData={imageData}
              videoThumbnails={videoThumbnails}
              threeSixtyImageThumbnails={threeSixtyImageThumbnails}
              selectedModalThumbnailIndex={selectedModalThumbnailIndex}
              showExpandedImageModal={showExpandedImageModal}
              data={props.data}
              handleModalTabChange={handleModalTabChange}
              modalTab={modalTab}
              onClose={handleClose}
              pinchToZoomImagesInitialized={pinchToZoomImagesInitialized}
              onPinchToZoomImageInit={onPinchToZoomImageInit}
              threeSixtySkuItem={
                threeSixtyImageData?.thumbnail
                  ? { thumbnail: threeSixtyImageData.thumbnail }
                  : undefined
              }
              threeSixtyUrls={urls}
              setSelectedPDPThumbnailIndex={setSelectedPDPThumbnailIndex}
              videoThumbnailsExist={videoThumbnailsExist}
              installationGuideExist={installationGuideEnabled}
              productVehicleImage={productVehicleImage}
              location={location ?? ''}
            />
          </Suspense>
        ) : null}
        {shouldDisplaySylvaniaInstallGuideBanner && (
          <YMMESpecificInstallVideoBanner
            data={sylvaniaData}
            onClick={() => {
              setModalTab('Installation Guide');
              handleOpenCarouselModal();
            }}
          />
        )}
        {shouldDisplayVehicleSpecificImagesBanner && (
          <VehicleSpecificImagesBanner onClick={handleVehicleSpecificImagesBannerClick} />
        )}
        <div
          className={cx(styles.desktopCarousel, {
            [styles.desktopCarouselV1Layout]: !isPdpRedesignEnabled,
          })}
        >
          <ThumbnailComponent
            classes={{
              thumbnailList: styles.pdpThumbnailContainer,
            }}
            imgTitle={itemDescription}
            onImageChange={showExpandedImagePDP}
            selectedThumbnailIndex={selectedPDPThumbnailIndex}
            handleSeeMoreClick={handleSeeMoreClick}
            handleThumbnailClick={handleThumbnailClick}
            loading={gcpProductsVideosResult.isFetching}
            items={pdpThumbnails}
            verticalLayout
            hoverEnabled
          />
          {!isMobileDevice ? (
            <div className={styles.desktopMedia}>
              <div className={styles.configurableSkuBadgeContainer}>
                {props.data.configurableSkuFlag && <ConfigurableSkuBadge />}
              </div>
              <LazyEnlargedMediaComponent
                enlargedMedia={enlargedMedia}
                handleOnKeyDown={handleOnKeyDown}
                onClick={handleOpenCarouselModal}
                caption={imageCaption}
                setAprimoVideoTag={setAprimoVideoTag}
                threeSixtyUrls={urls}
              />
            </div>
          ) : null}
        </div>
        <div className={styles.mobileCarousel}>
          {props.data.configurableSkuFlag && <ConfigurableSkuBadge />}
          <CarouselComponent
            imgTitle={itemDescription}
            items={pdpThumbnails}
            showMediumImageBeforeInteraction
            carouselCaption={imageCaption}
            onImageChange={showExpandedImagePDP}
            handleclick={handleOpenCarouselModal}
            selectedThumbnailIndex={selectedPDPThumbnailIndex}
            controlled
            useMagnify={false}
            verticalLayout={isPdpRedesignEnabled}
            classes={{ customCarouselContainer: styles.customCarouselContainer }}
          />
        </div>
      </div>
    );
  }

  return <div className={styles.noProdMsg}>{labels.label_CategoryPage_body_ImageNotAvailable}</div>;
};
